import React, { useState } from "react";
import { observer } from "mobx-react";
import {
  Button,
  Checkbox,
  Divider,
  Link,
  ListItem,
  Stack,
  Text,
  UnorderedList,
} from "@chakra-ui/react";
import { translations } from "@/constants/translations";
import { useStore } from "@/store";
import { renderInnerHtml } from "@/helpers/dom";
import { useAppNavigate } from "@/routing/useAppNavigate";
import { AppLink } from "@/routing/AppLink";
import { H2, H3 } from "@/components/Heading";
import { MailToLink } from "@/components/Link";
import { useEffectOnce } from "usehooks-ts";
import { telemetry } from "@/telemetry";
import { startRegistrationTimer } from "../helpers";

export const RegisterTermsPage = observer(() => {
  const { activeLanguage } = useStore();
  const language = translations.pages["register-terms"][activeLanguage];

  const [isChecked, setIsChecked] = useState(false);

  useEffectOnce(() => {
    startRegistrationTimer();
    telemetry.trackEvent("registration_terms_viewed");
  });

  const handleCheckboxChange = (event: any) => {
    setIsChecked(event.target.checked);
  };

  const navigate = useAppNavigate();
  const handleRedirect = () => navigate({ to: "/register/form" });

  return (
    <Stack spacing="14">
      <H2
        alignSelf="center"
        textAlign="center"
        maxW="container.md"
      >
        {renderInnerHtml(language.before_starting)}
      </H2>

      <Divider />

      <Stack spacing="6">
        <Stack mb="6">
          <H3>{renderInnerHtml(language.emergent_bioslutions)}</H3>
          <Text
            fontSize="18px"
            fontWeight="bold"
          >
            {renderInnerHtml(language.general_terms)}
          </Text>
          <Text
            fontSize="18px"
            fontStyle="italic"
            fontWeight="bold"
          >
            {renderInnerHtml(language.effective_date)}
          </Text>
        </Stack>

        <Stack>
          <Text
            fontWeight="bold"
            textDecoration="underline"
          >
            {renderInnerHtml(language.approval)}
          </Text>
          <UnorderedList pl="4">
            <ListItem>{renderInnerHtml(language.approval_point1)}</ListItem>
            <ListItem>{renderInnerHtml(language.approval_point2)}</ListItem>
          </UnorderedList>
        </Stack>

        <Stack>
          <Text
            fontWeight="bold"
            textDecoration="underline"
          >
            {renderInnerHtml(language.application)}
          </Text>
          <UnorderedList pl="4">
            <ListItem>{renderInnerHtml(language.application_point1)}</ListItem>
          </UnorderedList>
        </Stack>

        <Stack>
          <Text
            fontWeight="bold"
            textDecoration="underline"
          >
            {renderInnerHtml(language.payment_terms)}
          </Text>
          <UnorderedList pl="4">
            <ListItem>{renderInnerHtml(language.payment_point1)}</ListItem>
            <ListItem>{renderInnerHtml(language.payment_point2)}</ListItem>
            <ListItem>{renderInnerHtml(language.payment_point3)}</ListItem>
            <ListItem>{renderInnerHtml(language.payment_point4)}</ListItem>
          </UnorderedList>
        </Stack>

        <Stack>
          <Text
            fontWeight="bold"
            textDecoration="underline"
          >
            {renderInnerHtml(language.sales_data)}
          </Text>
          <UnorderedList pl="4">
            <ListItem>{renderInnerHtml(language.sales_data_point)}</ListItem>
          </UnorderedList>
        </Stack>

        <Stack>
          <Text
            fontWeight="bold"
            textDecoration="underline"
          >
            {renderInnerHtml(language.warranty)}
          </Text>
          <UnorderedList pl="4">
            <ListItem>{renderInnerHtml(language.warranty_point1)}</ListItem>
            <ListItem>{renderInnerHtml(language.warranty_point2)}</ListItem>
          </UnorderedList>
        </Stack>

        <Stack>
          <Text
            fontWeight="bold"
            textDecoration="underline"
          >
            {renderInnerHtml(language.indemnification)}
          </Text>
          <UnorderedList pl="4">
            <ListItem>
              {renderInnerHtml(language.indemnification_point1)}
            </ListItem>
            <ListItem>
              {renderInnerHtml(language.indemnification_point2)}
            </ListItem>
          </UnorderedList>
        </Stack>

        <Stack>
          <Text
            fontWeight="bold"
            textDecoration="underline"
          >
            {renderInnerHtml(language.obligations)}
          </Text>
          <UnorderedList pl="4">
            <ListItem>{renderInnerHtml(language.obligations_point1)}</ListItem>
            <ListItem>{renderInnerHtml(language.obligations_point2)}</ListItem>
          </UnorderedList>
        </Stack>

        <Stack>
          <Text
            fontWeight="bold"
            textDecoration="underline"
          >
            {renderInnerHtml(language.allocation)}
          </Text>
          <UnorderedList pl="4">
            <ListItem>{renderInnerHtml(language.allocation_point1)}</ListItem>
          </UnorderedList>
        </Stack>

        <Stack>
          <Text
            fontWeight="bold"
            textDecoration="underline"
          >
            {renderInnerHtml(language.exports)}
          </Text>
          <UnorderedList pl="4">
            <ListItem>
              {renderInnerHtml(language.exports_point1)}
              <UnorderedList pl="4">
                <ListItem>
                  {renderInnerHtml(language.exports_point1_1)}
                </ListItem>
                <ListItem>
                  {renderInnerHtml(language.exports_point1_2)}
                </ListItem>
              </UnorderedList>
            </ListItem>
          </UnorderedList>
        </Stack>

        <Stack>
          <Text
            fontWeight="bold"
            textDecoration="underline"
          >
            {renderInnerHtml(language.resale)}
          </Text>
          <UnorderedList pl="4">
            <ListItem>{renderInnerHtml(language.resale_point1)}</ListItem>
            <ListItem>{renderInnerHtml(language.resale_point2)}</ListItem>
            <ListItem>{renderInnerHtml(language.resale_point3)}</ListItem>
            <ListItem>{renderInnerHtml(language.resale_point4)}</ListItem>
          </UnorderedList>
        </Stack>

        <Stack>
          <Text
            fontWeight="bold"
            textDecoration="underline"
          >
            {renderInnerHtml(language.transportation)}
          </Text>
          <UnorderedList pl="4">
            <ListItem>
              {renderInnerHtml(language.transportation_point1)}
            </ListItem>
            <ListItem>
              {renderInnerHtml(language.transportation_point2)}
            </ListItem>
            <ListItem>
              {renderInnerHtml(language.transportation_point3)}
            </ListItem>
          </UnorderedList>
        </Stack>

        <Stack>
          <Text
            fontWeight="bold"
            textDecoration="underline"
          >
            {renderInnerHtml(language.return_refunds)}
          </Text>
          <UnorderedList pl="4">
            <ListItem>
              {renderInnerHtml(language.return_refunds_point1)}
            </ListItem>
          </UnorderedList>
        </Stack>

        <Stack>
          <Text
            fontWeight="bold"
            textDecoration="underline"
          >
            {renderInnerHtml(language.confidentiality)}
          </Text>
          <UnorderedList pl="4">
            <ListItem>
              {renderInnerHtml(language.confidentiality_point1)}
            </ListItem>
          </UnorderedList>
        </Stack>

        <Stack>
          <Text
            fontWeight="bold"
            textDecoration="underline"
          >
            {renderInnerHtml(language.audit)}
          </Text>
          <UnorderedList pl="4">
            <ListItem>{renderInnerHtml(language.audit_point1)}</ListItem>
          </UnorderedList>
        </Stack>

        <Stack>
          <Text
            fontWeight="bold"
            textDecoration="underline"
          >
            {renderInnerHtml(language.adverse_event)}
          </Text>
          <UnorderedList pl="4">
            <ListItem>
              {renderInnerHtml(language.adverse_event_point1)}
            </ListItem>
          </UnorderedList>
        </Stack>

        <Stack>
          <Text
            fontWeight="bold"
            textDecoration="underline"
          >
            {renderInnerHtml(language.medical)}
          </Text>
          <UnorderedList pl="4">
            <ListItem>
              {renderInnerHtml(language.medical_point, [
                <Link
                  key="1"
                  fontWeight="bold"
                  href={`tel:${language.medical_phone}`}
                >
                  {renderInnerHtml(language.medical_phone)}
                </Link>,
                <MailToLink
                  key="2"
                  textDecoration="underline"
                  href={language.medical_email}
                >
                  {renderInnerHtml(language.medical_email)}
                </MailToLink>,
              ])}
            </ListItem>
          </UnorderedList>
        </Stack>

        <Stack>
          <Text
            fontWeight="bold"
            textDecoration="underline"
          >
            {renderInnerHtml(language.ordering)}
          </Text>
          <UnorderedList pl="4">
            <ListItem>
              {renderInnerHtml(language.ordering_point1)}
              <Link
                fontWeight="bold"
                href={`tel: ${language.ordering_phone}`}
              >
                {renderInnerHtml(language.ordering_phone)}
              </Link>
              {renderInnerHtml(language.ordering_point1_2)}
              <MailToLink
                textDecoration="underline"
                href={language.ordering_email}
              >
                {renderInnerHtml(language.ordering_email)}
              </MailToLink>
              {renderInnerHtml(language.ordering_point1_3)}
            </ListItem>
          </UnorderedList>
        </Stack>

        <Stack>
          <Text
            fontWeight="bold"
            textDecoration="underline"
          >
            {renderInnerHtml(language.governing)}
          </Text>
          <UnorderedList pl="4">
            <ListItem>{renderInnerHtml(language.governing_point1)}</ListItem>
            <ListItem>{renderInnerHtml(language.governing_point2)}</ListItem>
          </UnorderedList>
        </Stack>

        <Stack>
          <Text
            fontWeight="bold"
            textDecoration="underline"
          >
            {renderInnerHtml(language.changes)}
          </Text>
          <UnorderedList pl="4">
            <ListItem>
              {renderInnerHtml(language.changes_point1)}
              <Link
                href={language.changes_link}
                target="_blank"
                rel="noopener noreferrer"
                textDecoration="underline"
              >
                {renderInnerHtml(language.changes_link)}
              </Link>
              {renderInnerHtml(language.changes_point1_2)}
            </ListItem>
          </UnorderedList>
        </Stack>
      </Stack>
      <Divider />

      <Checkbox
        id="confirmRead"
        onChange={handleCheckboxChange}
      >
        {renderInnerHtml(language.i_acknowledge)}
        <AppLink
          to="/general-terms"
          target="_blank"
          rel="noopener noreferrer"
        >
          {renderInnerHtml(language.general_terms_link)}
        </AppLink>
        .
      </Checkbox>

      <Button
        alignSelf="center"
        onClick={handleRedirect}
        isDisabled={!isChecked}
      >
        {renderInnerHtml(language.begin_registration)}
      </Button>
    </Stack>
  );
});
